// The v2 inventory grid's column registry (docs/decisions/inventory-columns.md).
//
// Keys are stable: they are what a user's saved layout (the user setting
// `grid.inventory_columns`, an ordered list of { key, visible }) refers to.
// Fixed columns (aging dot, Stock #, row actions) are always shown, never
// listed in the Columns picker and never move. The rest can be shown,
// hidden and reordered. The default layout is today's grid; new columns
// arrive hidden.
//
// `sortKey` is the unit field (or computed key) the column sorts by; it is
// also the DataTable column key, so sorting works as before.

import { AI_GAP_SORT_KEY, DEFAULT_SORT } from './inventoryView';
import { DEAL_STATUS_SORT_KEY } from './dealStatus';

export const COLUMN_LAYOUT_KEY = 'grid.inventory_columns';

export const INVENTORY_COLUMNS = Object.freeze([
    { key: 'aging', label: 'Aging', fixed: 'start' },
    { key: 'stock', label: 'Stock #', fixed: 'start', sortKey: 'StockNumber' },
    { key: 'equipment', label: 'Equipment', sortKey: 'MAKE', defaultVisible: true },
    { key: 'branch', label: 'Branch', sortKey: 'BranchName', defaultVisible: true },
    { key: 'days', label: 'Days', sortKey: 'DaysInStock', defaultVisible: true },
    { key: 'price', label: 'Price', sortKey: 'Best_Sell_Price', defaultVisible: true },
    { key: 'aiPrice', label: 'AI Price', sortKey: AI_GAP_SORT_KEY, defaultVisible: true },
    { key: 'trueCost', label: 'True Cost', sortKey: 'CarryingCostBasis', defaultVisible: true },
    { key: 'risk', label: 'Risk', sortKey: 'LadenhueterRisk', defaultVisible: true },
    { key: 'interest', label: 'Interest', sortKey: 'QuoteCount', defaultVisible: true },
    { key: 'lastQuote', label: 'Last Quote', sortKey: 'LastQuotedGapPct', defaultVisible: true },
    { key: 'hours', label: 'Hours', sortKey: 'ODOMETER', defaultVisible: false },
    { key: 'dealStatus', label: 'Deal status', sortKey: DEAL_STATUS_SORT_KEY, defaultVisible: false },
    { key: 'actions', label: 'Actions', fixed: 'end' },
]);

const BY_KEY = Object.freeze(Object.fromEntries(INVENTORY_COLUMNS.map((c) => [c.key, c])));

export const columnDef = (key) => BY_KEY[key] || null;
export const columnLabel = (key) => BY_KEY[key]?.label || key;

export const FIXED_COLUMN_KEYS = Object.freeze(INVENTORY_COLUMNS.filter((c) => c.fixed).map((c) => c.key));
const FIXED_START = INVENTORY_COLUMNS.filter((c) => c.fixed === 'start').map((c) => c.key);
const FIXED_END = INVENTORY_COLUMNS.filter((c) => c.fixed === 'end').map((c) => c.key);
export const MOVABLE_COLUMN_KEYS = Object.freeze(INVENTORY_COLUMNS.filter((c) => !c.fixed).map((c) => c.key));

export const DEFAULT_COLUMN_LAYOUT = Object.freeze(
    MOVABLE_COLUMN_KEYS.map((key) => Object.freeze({ key, visible: BY_KEY[key].defaultVisible })),
);

const isMovable = (key) => typeof key === 'string' && MOVABLE_COLUMN_KEYS.includes(key);

/**
 * A saved layout as the grid can use it. Forward compatible, the same rule
 * as the backend's resolver: entries with an unknown (or fixed) key,
 * duplicates and malformed entries are dropped; every movable key missing
 * from the list is inserted at its default position, i.e. right after the
 * key that precedes it in the default order (first if none does), with
 * its default visibility. An entry whose `visible` is not a boolean is
 * malformed (dropped, then re-inserted as missing). Anything but a list is
 * the default layout.
 */
export const normalizeColumnLayout = (saved) => {
    if (!Array.isArray(saved)) return DEFAULT_COLUMN_LAYOUT.map((c) => ({ ...c }));
    const out = [];
    const seen = new Set();
    saved.forEach((entry) => {
        const key = entry && typeof entry === 'object' ? entry.key : null;
        if (!isMovable(key) || seen.has(key) || typeof entry.visible !== 'boolean') return;
        seen.add(key);
        out.push({ key, visible: entry.visible });
    });
    MOVABLE_COLUMN_KEYS.forEach((key, i) => {
        if (seen.has(key)) return;
        const before = i > 0 ? MOVABLE_COLUMN_KEYS[i - 1] : null;
        const at = before ? out.findIndex((c) => c.key === before) + 1 : 0;
        out.splice(at, 0, { key, visible: BY_KEY[key].defaultVisible });
        seen.add(key);
    });
    return out;
};

export const isDefaultLayout = (layout) => {
    const norm = normalizeColumnLayout(layout);
    return norm.every((c, i) => c.key === DEFAULT_COLUMN_LAYOUT[i].key && c.visible === DEFAULT_COLUMN_LAYOUT[i].visible);
};

export const setColumnVisible = (layout, key, visible) => normalizeColumnLayout(layout)
    .map((c) => (c.key === key ? { ...c, visible: Boolean(visible) } : c));

/** Move `key` to index `to` (clamped). */
export const moveColumnTo = (layout, key, to) => {
    const list = normalizeColumnLayout(layout);
    const from = list.findIndex((c) => c.key === key);
    if (from < 0) return list;
    const target = Math.max(0, Math.min(list.length - 1, to));
    if (target === from) return list;
    const [item] = list.splice(from, 1);
    list.splice(target, 0, item);
    return list;
};

/** Move `key` by `delta` places (−1 up, +1 down), clamped at the ends. */
export const moveColumn = (layout, key, delta) => {
    const from = normalizeColumnLayout(layout).findIndex((c) => c.key === key);
    return moveColumnTo(layout, key, from + delta);
};

/**
 * Re-apply a change made on `start` (start → next) to `fresh`, a newer
 * starting layout (fresh QA F-1: the role preset was edited meanwhile).
 * Each column whose visibility the user flipped takes the user's value;
 * the rest keeps the fresh layout's. A changed order takes the user's
 * order, an unchanged one keeps the fresh order.
 */
export const rebaseColumnLayout = (start, next, fresh) => {
    const from = normalizeColumnLayout(start);
    const to = normalizeColumnLayout(next);
    const was = Object.fromEntries(from.map((c) => [c.key, c.visible]));
    const flipped = Object.fromEntries(to.filter((c) => c.visible !== was[c.key]).map((c) => [c.key, c.visible]));
    const reordered = from.some((c, i) => c.key !== to[i].key);
    const base = normalizeColumnLayout(fresh);
    const freshVisible = Object.fromEntries(base.map((c) => [c.key, c.visible]));
    const order = reordered ? to.map((c) => c.key) : base.map((c) => c.key);
    return order.map((key) => ({ key, visible: key in flipped ? flipped[key] : freshVisible[key] }));
};

/**
 * Every column the grid shows, in order: the fixed start columns, the
 * visible movable ones, the fixed end column. `force` shows those keys in
 * their layout place even when hidden (Deal status while committed units
 * are included), without touching the layout itself.
 */
export const visibleColumnKeys = (layout, { force = [] } = {}) => [
    ...FIXED_START,
    ...normalizeColumnLayout(layout).filter((c) => c.visible || force.includes(c.key)).map((c) => c.key),
    ...FIXED_END,
];

/** The sort keys of these registry keys (columns without one are skipped). */
export const sortKeysFor = (keys) => new Set(keys.map((k) => BY_KEY[k]?.sortKey).filter(Boolean));

/**
 * The sort the grid applies: `sort` while its column is shown, else the
 * default sort (which holds even with Days hidden).
 */
export const effectiveSort = (sort, visibleKeys, defaultSort = DEFAULT_SORT) => {
    if (!sort || !sort.key) return defaultSort;
    if (sort.key === defaultSort.key && sort.direction === defaultSort.direction) return sort;
    return sortKeysFor(visibleKeys).has(sort.key) ? sort : defaultSort;
};
